import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { get, includes, some } from 'lodash';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import { IconButton } from '@material-ui/core/';
import { Help as HelpIcon } from '@material-ui/icons';

import { LanguageSelector } from 'core';
import { Locale } from '@lib/providers/LanguageProvider';
import configService from '@lib/services/configService';
import HeaderUserMenuButton from 'components/HeaderUserMenuButton';
import LaunchForms from 'components/LaunchForms';
import NotificationsCenter from 'components/NotificationsCenter';
import GlobalSearch from 'components/GlobalSearch';
import Tooltip from 'components/Tooltip';
import { hasAccessToProduct } from 'utils/permissions';
import { ProvisioningEnum } from '@lib/resources/types';
import { signOut } from '@lib/actions';
import { selectCurrentUser, selectCurrentMaster, selectLocale } from '@lib/selectors';
import { selectPermissions } from 'selectors';
import authResource from 'resources/authResource';
import { changeFavicon, changeTabTitle, normalizeDOB } from 'ui-utils';
import { muiThemeLuma } from 'themes';
import { isCHC } from 'utils/constants';
import { BrandingContext } from 'containers/App/contexts';
import { defaultCurrentDomainLogo, defaultCurrentDomainLogoWhite } from 'containers/App/contexts/BrandingContext';

import {
  makeSelectAccounts,
  makeSelectLoadingNotifications,
  makeSelectNotifications,
  makeSelectNotificationsPage,
  makeSelectData,
  makeSelectOrganization,
} from './selectors';
import {
  clearUnreadSystemNotifications,
  loadAccounts,
  loadData,
  loadNotifications,
  saveReferral,
  switchAccount,
} from './actions';
import { withContext } from './HeaderWrapper';
import { getLogoPath } from './utils';
import { CHC_TITLE } from './constants';

import styles from './Header.styles';
import { NextyBanner } from './components';

class Header extends Component {
  UNSAFE_componentWillMount() {
    const { dispatchLoadHeaderInformation } = this.props;
    dispatchLoadHeaderInformation();
  }

  handleClickHelpCenter = () => {
    const { currentUser, brandingContextValues } = this.props;
    const { isCobrandingEnabled, cobrandingCustomStyles } = brandingContextValues;
    const cobrandingHelpCenterURL = cobrandingCustomStyles?.styles?.theme?.uiSettings?.toolbarHelpCenterUrl;

    const useCobrandingHelpCenterURL = isCobrandingEnabled && !!cobrandingHelpCenterURL;

    if (useCobrandingHelpCenterURL) {
      return window.open(cobrandingHelpCenterURL);
    }

    const user = {
      email: currentUser.email,
      name: `${currentUser.firstname} ${currentUser.lastname}`,
    };

    authResource.zendeskSSO(user, 'https://lumahealth.zendesk.com/hc/en-us');
  };

  handleLoadMoreNotifications = () => {
    const { currentNotificationsPage, dispatchLoadMoreNotifications } = this.props;

    if (currentNotificationsPage > 0) dispatchLoadMoreNotifications(currentNotificationsPage + 1);
  };

  handleSwitchAccount = (account) => {
    const { dispatchSwitchAccount, organization } = this.props;

    dispatchSwitchAccount(organization, account);
  };

  renderUserMenu() {
    const {
      accounts,
      currentUser,
      root,
      dispatchSignOut,
      permissions,
      menuButtonVariant,
      brandingContextValues,
      nextyContextValues,
    } = this.props;
    const toggleNextyNext = nextyContextValues?.toggleNextyNextStorage;

    const enableViewTicket = get(
      brandingContextValues,
      'cobrandingCustomStyles.styles.theme.uiSettings.enableViewTicket',
      true,
    );

    return (
      <HeaderUserMenuButton
        avatar={menuButtonVariant === 'avatar'}
        permissions={permissions}
        accounts={accounts || []}
        toggleNextyNext={toggleNextyNext}
        enableViewTicket={enableViewTicket}
        onSignout={dispatchSignOut}
        onSwitchAccount={this.handleSwitchAccount}
        user={currentUser}
        root={root}
      />
    );
  }

  renderNotifications() {
    const {
      currentNotificationsPage,
      loadingNotifications,
      dispatchClearUnreadNotifications,
      dispatchReferralCalledMessageAction,
      notifications,
      variant,
    } = this.props;
    const hasMoreNotifications = !loadingNotifications && currentNotificationsPage !== 0;

    return (
      <NotificationsCenter
        iconColor={variant === 'dark' ? 'white' : muiThemeLuma.palette.primary.main}
        hasMore={hasMoreNotifications}
        notifications={notifications}
        onLoadMore={this.handleLoadMoreNotifications}
        onNotificationsCenterIconClick={dispatchClearUnreadNotifications}
        onReferralCalledMessageAction={dispatchReferralCalledMessageAction}
      />
    );
  }

  renderHelpLink() {
    const { classes, variant } = this.props;

    return (
      <Tooltip className={classes.tooltip} hideArrow title="Help Center" placement="bottom">
        <IconButton
          aria-label="Help"
          style={variant === 'dark' ? { color: 'white' } : { color: muiThemeLuma.palette.primary.main }}
          className={classes.helpCenter}
          color="primary"
          onClick={this.handleClickHelpCenter}
        >
          <HelpIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  handleSearch = (text) => {
    const { dispatchSearchData, currentLocale } = this.props;
    const searchText = normalizeDOB(text, currentLocale);
    dispatchSearchData(searchText);
  };

  renderGlobalSearch() {
    const { data, currentLocale } = this.props;
    const localization = currentLocale || Locale.EnglishUS;

    return <GlobalSearch onSearch={this.handleSearch} data={data} localization={localization} />;
  }

  renderLogo() {
    const { classes, currentUser, variant, brandingContextValues } = this.props;
    const { isCobrandingEnabled, cobrandingLogo, cobrandingFavicon } = brandingContextValues;

    // TODO use `hasAccessToProduct`
    const provisioning = get(currentUser, 'salesforceData.provisioning');
    const hasOnlyTelehealth = includes(provisioning, 'telehealth') && provisioning.length === 1;
    const hasOnlyChat = includes(provisioning, 'chat') && provisioning.length === 1;
    const hasOnlyRecallUpload =
      provisioning.length === 2 && includes(provisioning, 'recall') && includes(provisioning, 'upload');
    const hasNoAccessToActivityFeed =
      provisioning.length === 1 &&
      some(['followup', 'upload', 'branding', 'insurance', 'recall'], (item) => includes(provisioning, item));

    const hrefPath = getLogoPath({ hasOnlyTelehealth, hasOnlyChat, hasOnlyRecallUpload, hasNoAccessToActivityFeed });

    const canUseCobrandingLogo = !!cobrandingLogo?.url && isCobrandingEnabled;

    const CurrentDomainLogoToUse = variant === 'dark' ? defaultCurrentDomainLogoWhite : defaultCurrentDomainLogo;
    const logoToUse = canUseCobrandingLogo ? cobrandingLogo.url : CurrentDomainLogoToUse;

    if (isCobrandingEnabled && cobrandingFavicon) {
      changeFavicon(cobrandingFavicon.url);
    }
    if (isCHC) {
      changeTabTitle(CHC_TITLE);
    }

    return (
      <div className={classes.logoWrapper}>
        <a href={hrefPath}>
          <img className={classes.logo} src={logoToUse} alt="Logo" />
        </a>
      </div>
    );
  }

  render() {
    const { classes, variant, permissions, brandingContextValues, nextyContextValues } = this.props;
    const showLaunchForms = hasAccessToProduct(permissions, ProvisioningEnum.PrequalForm);
    const showNextyBanner = variant !== 'dark' && !nextyContextValues.shouldHideNextyBanner;

    return (
      <>
        <div className={classnames(classes.root, 'no-print', { [classes.dark]: variant === 'dark' })}>
          {this.renderLogo()}
          {this.renderGlobalSearch()}
          {showLaunchForms && (
            <LaunchForms iconColor={variant === 'dark' ? 'white' : muiThemeLuma.palette.primary.main} />
          )}
          <BrandingContext.Consumer>
            {(props) => {
              const enableHelpCenter = get(
                props,
                'cobrandingCustomStyles.styles.theme.uiSettings.enableHelpCenter',
                true,
              );
              if (enableHelpCenter || !get(props, 'isCobrandingEnabled', false)) {
                return this.renderHelpLink();
              }
            }}
          </BrandingContext.Consumer>
          {this.renderNotifications()}
          {this.renderUserMenu()}
          {/* TODO danielmana use rollout enum */}
          {hasAccessToProduct(permissions, 'language-selector') && <LanguageSelector />}
        </div>
        {showNextyBanner && <NextyBanner key={Number(showNextyBanner)} />}
      </>
    );
  }
}

Header.propTypes = {
  accounts: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  classes: PropTypes.object.isRequired,
  currentNotificationsPage: PropTypes.number,
  currentUser: PropTypes.object.isRequired,
  dispatchClearUnreadNotifications: PropTypes.func.isRequired,
  dispatchLoadHeaderInformation: PropTypes.func.isRequired,
  dispatchLoadMoreNotifications: PropTypes.func.isRequired,
  dispatchReferralCalledMessageAction: PropTypes.func.isRequired,
  dispatchSearchData: PropTypes.func.isRequired,
  dispatchSignOut: PropTypes.func.isRequired,
  dispatchSwitchAccount: PropTypes.func.isRequired,
  loadingNotifications: PropTypes.bool,
  notifications: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  organization: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.bool, PropTypes.object]),
  variant: PropTypes.string,
  menuButtonVariant: PropTypes.string,
};

Header.defaultProps = {
  accounts: false,
  currentNotificationsPage: 1,
  loadingNotifications: false,
  notifications: false,
  organization: false,
  data: false,
  variant: 'default',
  menuButtonVariant: 'default',
};

const mapStateToProps = createStructuredSelector({
  permissions: selectPermissions,
  accounts: makeSelectAccounts(),
  currentNotificationsPage: makeSelectNotificationsPage(),
  currentUser: selectCurrentUser,
  root: selectCurrentMaster,
  currentLocale: selectLocale,
  loadingNotifications: makeSelectLoadingNotifications(),
  notifications: makeSelectNotifications(),
  organization: makeSelectOrganization(),
  data: makeSelectData(),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchClearUnreadNotifications: () => {
    dispatch(clearUnreadSystemNotifications());
  },
  dispatchLoadHeaderInformation: () => {
    dispatch(loadAccounts());
    dispatch(loadNotifications(1));
    dispatch(loadData());
  },
  dispatchLoadMoreNotifications: (page) => {
    dispatch(loadNotifications(page));
  },
  dispatchReferralCalledMessageAction: (referral) => {
    dispatch(saveReferral(referral));
  },
  dispatchSearchData: (searchText) => {
    dispatch(loadData(searchText));
  },
  dispatchSignOut: () => {
    dispatch(signOut({}));
  },
  dispatchSwitchAccount: (organization, account) => {
    dispatch(switchAccount(organization, account));
  },
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const HeaderWrapped = withContext(Header);
export default compose(withConnect, withStyles(styles))(HeaderWrapped);
